/* eslint-disable no-restricted-syntax, no-continue, no-plusplus */
/**
 * Deep recursive diff utility for comparing entity snapshots.
 *
 * Produces a Map of dot-notation paths to { oldValue, newValue } pairs.
 * Arrays are matched by `id` field when available, otherwise by index.
 */

export interface FieldChange {
  oldValue: unknown;
  newValue: unknown;
}

const SKIP_FIELDS = new Set(['id', 'matter_id']);

export function computeDeepDiffs(
  oldVal: unknown,
  curVal: unknown,
  prefix = '',
): Map<string, FieldChange> {
  const changes = new Map<string, FieldChange>();

  function makePath(base: string, key: string | number): string {
    return base ? `${base}.${key}` : String(key);
  }

  function diff(o: unknown, c: unknown, p: string): void {
    if (o == null && c == null) return;

    if (o == null || c == null) {
      changes.set(p, { oldValue: o, newValue: c });
      return;
    }

    if (Array.isArray(o) && Array.isArray(c)) {
      diffArrays(o, c, p);
      return;
    }

    if (
      typeof o === 'object' &&
      typeof c === 'object' &&
      !Array.isArray(o) &&
      !Array.isArray(c)
    ) {
      const oObj = o as Record<string, unknown>;
      const cObj = c as Record<string, unknown>;
      const keys = new Set([...Object.keys(oObj), ...Object.keys(cObj)]);
      for (const key of keys) {
        if (SKIP_FIELDS.has(key)) continue;
        diff(oObj[key], cObj[key], makePath(p, key));
      }
      return;
    }

    if (JSON.stringify(o) !== JSON.stringify(c)) {
      changes.set(p, { oldValue: o, newValue: c });
    }
  }

  function diffArrays(o: unknown[], c: unknown[], p: string): void {
    const hasIds = [...o, ...c].some(
      (item) => typeof item === 'object' && item !== null && 'id' in item,
    );

    if (hasIds) {
      const getId = (item: unknown): string | undefined =>
        typeof item === 'object' && item !== null && 'id' in item
          ? String((item as Record<string, unknown>).id)
          : undefined;

      const oldById = new Map<string, { item: unknown; idx: number }>();
      o.forEach((item, idx) => {
        const id = getId(item);
        if (id) oldById.set(id, { item, idx });
      });

      const curById = new Map<string, { item: unknown; idx: number }>();
      c.forEach((item, idx) => {
        const id = getId(item);
        if (id) curById.set(id, { item, idx });
      });

      for (const [id, { item, idx }] of curById) {
        if (!oldById.has(id)) {
          changes.set(makePath(p, idx), {
            oldValue: undefined,
            newValue: item,
          });
        }
      }

      for (const [id, { item }] of oldById) {
        if (!curById.has(id)) {
          changes.set(makePath(p, `_removed_${id}`), {
            oldValue: item,
            newValue: undefined,
          });
        }
      }

      for (const [id, { item: curItem, idx }] of curById) {
        const oldEntry = oldById.get(id);
        if (oldEntry) {
          diff(oldEntry.item, curItem, makePath(p, idx));
        }
      }
    } else {
      const maxLen = Math.max(o.length, c.length);
      for (let i = 0; i < maxLen; i++) {
        diff(o[i], c[i], makePath(p, i));
      }
    }
  }

  diff(oldVal, curVal, prefix);
  return changes;
}
